// yearRange.js — THE year range for every value-year selector in the app.
//
// ⛔ ONE SOURCE. Before this, twelve screens each derived their own range and no two agreed: the three
// tax-bill screens computed `getFullYear() + 1 - i` over 5 items (2023-2027 today — anchored to nothing,
// so it silently DROPS 2023 next January), DocumentScanner hardcoded ['2026'…'2022'] (which is also why a
// 2027 bill could not be scanned), and seven others carried a literal 2021-2028 array. Ceilings ranged
// from 2026 to 2029. The range now comes from folder_year_config — the DB row Admin → Year Management
// already edits, and which ParcelModal and AdminPanel already honoured.
//
// FALLBACK 2021-2028 when the row can't be read. Deliberately the same array those seven screens carried,
// so an unreachable config degrades to today's behaviour rather than to an empty selector.
//
// ⛔ NOT FOR DOCUMENT FILING YEARS. docFiling.js FILING_YEARS and DocumentIntake's YEARS run 2018-2029 on
// purpose: a contract or an old notice legitimately predates the tax-data cutoff, and filing must not be
// capped by the value-year range. Those stay separate — see the note in docFiling.js.
//
// ⛔ NOT FOR ROLLING WINDOWS. StaffPortal's billCountYears ([current-4 … current+1]) is a deliberate
// rolling window for the dashboard's CS bill count, not a year picker. Leave it.
import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export const FALLBACK_MIN_YEAR = 2021;
export const FALLBACK_MAX_YEAR = 2028;

const buildYears = (min, max) => {
  const lo = Number(min), hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) {
    return buildYears(FALLBACK_MIN_YEAR, FALLBACK_MAX_YEAR);
  }
  return Array.from({ length: hi - lo + 1 }, (_, i) => String(lo + i));
};

export const FALLBACK_YEARS = buildYears(FALLBACK_MIN_YEAR, FALLBACK_MAX_YEAR);

// Module-level cache + in-flight de-dup: a dozen components mounting must not each hit the table, and two
// mounting at once must share one request. Same shape as fileUrl's cache, for the same reason.
let _cache = null;
let _inflight = null;

async function fetchRange() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    const { data, error } = await supabase
      .from('folder_year_config')
      .select('min_year, current_max_year, current_value_year')
      .maybeSingle();
    // A failed read falls back rather than throwing: a year selector that renders nothing is worse than
    // one showing the default span. The warning is the signal that the config is unreachable.
    if (error || !data) {
      if (error) console.warn('[yearRange] folder_year_config read failed — using the fallback span:', error.message);
      return { years: FALLBACK_YEARS, minYear: FALLBACK_MIN_YEAR, maxYear: FALLBACK_MAX_YEAR, currentValueYear: null, fromConfig: false };
    }
    _cache = {
      years: buildYears(data.min_year, data.current_max_year),
      minYear: Number(data.min_year),
      maxYear: Number(data.current_max_year),
      currentValueYear: data.current_value_year != null ? String(data.current_value_year) : null,
      fromConfig: true,
    };
    return _cache;
  })().finally(() => { _inflight = null; });
  return _inflight;
}

// Hook form — what the selectors use. Renders the fallback span on the first paint, then the configured
// span once it resolves; never renders an empty list.
export function useYearRange() {
  const [range, setRange] = useState(
    _cache || { years: FALLBACK_YEARS, minYear: FALLBACK_MIN_YEAR, maxYear: FALLBACK_MAX_YEAR, currentValueYear: null, fromConfig: false }
  );
  useEffect(() => {
    let alive = true;
    fetchRange().then(r => { if (alive) setRange(r); });
    return () => { alive = false; };
  }, []);
  return range;
}
